// src/components/CollectionReadinessCard.jsx
// Collection Readiness (§9.1) — dashboard collectionneur.
// « Voilà l'état patrimonial de votre collection, son niveau de préparation
//   et ce qui nécessite votre attention. »
// Utilise le moteur commun src/utils/readiness.js (§9.5). i18n FR / EN / DE.
import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { useAuth } from "../context/AuthContext";
import { supabase } from "../lib/supabaseClient";
import { computeCollectionReadiness, READINESS_SELECT } from "../utils/readiness";
import { applyProposalFilter, proposalCollectionIds } from "../lib/proposalExclusion";

const tone = (pct) =>
  pct >= 80 ? { bar: "bg-emerald-500", text: "text-emerald-700" }
  : pct >= 50 ? { bar: "bg-amber-500", text: "text-amber-700" }
  : { bar: "bg-rose-500", text: "text-rose-700" };

export default function CollectionReadinessCard({ userId, collectionId }) {
  const { t } = useTranslation();
  const { user } = useAuth();
  const ownerId = userId || user?.id || null;
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    let alive = true;
    if (!ownerId) { setLoading(false); return; }
    (async () => {
      setLoading(true);
      let q = supabase.from("artworks").select(READINESS_SELECT).eq("client_id", ownerId);
      if (collectionId) q = q.eq("collection_id", collectionId);
      else q = applyProposalFilter(q, await proposalCollectionIds(ownerId));
      const { data: rows, error } = await q;
      if (!alive) return;
      if (error) { console.error("[CollectionReadinessCard]", error); setData(null); }
      else setData(computeCollectionReadiness(rows || []));
      setLoading(false);
    })();
    return () => { alive = false; };
  }, [ownerId, collectionId]);

  if (loading || !data || data.total === 0) return null;

  const overallTone = tone(data.overall);

  return (
    <section className="mb-6 rounded-2xl border border-gray-200 bg-white p-6 shadow-sm">
      {/* En-tête + score global */}
      <div className="flex flex-wrap items-start justify-between gap-4">
        <div className="min-w-0">
          <p className="text-xs font-semibold uppercase tracking-[0.14em] text-gray-400">{t("readiness.cards.collection.eyebrow")}</p>
          <h2 className="mt-1 font-serif text-xl font-semibold text-gray-950" style={{ fontFamily: "Georgia, serif" }}>
            {t("readiness.cards.collection.title")}
          </h2>
          <p className="mt-1 text-sm text-gray-500">
            {t("readiness.cards.collection.subtitle", { count: data.total })}
          </p>
        </div>
        <div className="text-right">
          <div className={`text-4xl font-semibold ${overallTone.text}`}>{data.overall}%</div>
          <div className="text-[11px] uppercase tracking-wide text-gray-400">{t("readiness.cards.collection.readinessLabel")}</div>
        </div>
      </div>

      {/* Dimensions */}
      <div className="mt-5 grid grid-cols-1 gap-x-8 gap-y-3 sm:grid-cols-2">
        {data.dimensions.map((d) => {
          const dimensionTone = tone(d.pct);
          return (
            <div key={d.key}>
              <div className="mb-1 flex items-baseline justify-between">
                <span className="text-sm text-gray-700">{t(`readiness.dimensions.${d.key}`)}</span>
                <span className={`text-sm font-medium ${dimensionTone.text}`}>{d.pct}%</span>
              </div>
              <div className="h-1.5 w-full overflow-hidden rounded-full bg-gray-100">
                <div className={`h-full rounded-full ${dimensionTone.bar}`} style={{ width: `${d.pct}%` }} />
              </div>
            </div>
          );
        })}
      </div>

      {/* Œuvres exceptionnelles non préparées (risque prioritaire §9.1) */}
      {data.exceptionalUnprepared > 0 && (
        <div className="mt-5 rounded-lg border border-rose-200 bg-rose-50 px-4 py-3 text-sm text-rose-800">
          {t("readiness.cards.collection.exceptional", { count: data.exceptionalUnprepared })}
        </div>
      )}

      {/* Actions prioritaires */}
      {data.actions.length > 0 && (
        <div className="mt-5">
          <p className="mb-2 text-xs font-semibold uppercase tracking-wide text-gray-400">{t("readiness.cards.collection.actionsTitle")}</p>
          <ul className="space-y-1.5">
            {data.actions.slice(0, 6).map((a) => {
              const pc = a.priority === "critical" ? "#b3261e" : a.priority === "recommended" ? "#a9843f" : "#9ca3af";
              return (
                <li key={a.key} className="flex items-center gap-2 text-sm text-gray-700">
                  <span
                    title={t(`readiness.priorityLevel.${a.priority}`, { defaultValue: a.priority === "critical" ? "Critique" : a.priority === "recommended" ? "Recommandé" : "Information" })}
                    style={{ width: 8, height: 8, borderRadius: 9999, background: pc, flexShrink: 0 }}
                  />
                  <span
                    className="inline-flex h-5 min-w-[1.5rem] items-center justify-center rounded-full px-1.5 text-[11px] font-medium text-white"
                    style={{ background: pc }}
                  >
                    {a.count}
                  </span>
                  {t(`readiness.actions.${a.key}`, { count: a.count })}
                </li>
              );
            })}
          </ul>
          <Link to="/my-collection" className="mt-3 inline-block text-sm font-medium text-gray-900 underline hover:text-gray-600">
            {t("readiness.cards.collection.viewCollection")}
          </Link>
        </div>
      )}
    </section>
  );
}
